const PatPromptTemplates = () => {
  return (
    <>
      <p>
        <em>
          The quality of AI-generated clinical documentation depends entirely on
          the prompt. In psychedelic-assisted therapy, where the clinical
          phenomena are unlike anything in conventional practice, generic
          prompts produce generic output — and generic output is worse than
          useless. It misrepresents the work, creates audit risk, and fails
          the client. This guide introduces the MycenAI Clinical Prompt Library
          and explains how structured, PAT-specific prompt templates produce
          documentation that actually reflects the complexity of what happens
          in the room.
        </em>
      </p>

      <hr />

      <h2>Why Prompts Matter More in PAT Than Anywhere Else</h2>

      <p>
        If you have used a general-purpose AI tool to draft a clinical note,
        you already know what happens. You get something plausible but flat.
        The language is correct but not clinically precise. The structure is
        recognisable but generic. It reads like documentation written by
        someone who has never sat with a client — because it was.
      </p>

      <p>
        In conventional therapy, this is a nuisance. You spend ten minutes
        editing a draft that should have been right the first time. In
        psychedelic-assisted therapy, it is a genuine problem. PAT
        documentation must capture phenomena that most clinical frameworks
        were never designed to describe: non-ordinary states of consciousness,
        somatic release patterns, ego dissolution, mystical-type experiences,
        challenging psychological content that unfolds in real time, and
        integration processes that span weeks or months after a single dosing
        session.
      </p>

      <p>
        A standard SOAP or DAP note template does not have fields for these experiences. A generic AI prompt
        will not generate them. The result is documentation that either omits
        the most clinically significant material or shoehorns it into
        categories where it does not belong.
      </p>

      <p>
        The fix is not better AI models. It is better prompts. A well-structured
        prompt tells the AI exactly what clinical territory to cover, what
        terminology to use, what structure the output should follow, and what
        therapeutic orientation to write from. The prompt is the clinical
        framework. The AI is just the engine.
      </p>

      <h2>The PAT Treatment Arc: Where Templates Fit</h2>

      <p>
        Psychedelic-assisted therapy follows a distinctive treatment arc that
        differs from conventional weekly-session psychotherapy. Each phase has
        its own documentation requirements, and each document in the chain
        depends on what came before. Understanding this arc is essential to
        understanding why PAT-specific prompt templates exist and where they
        fit in the clinical workflow.
      </p>

      <h3>Phase 1: Intake and Assessment</h3>

      <p>
        The intake phase in PAT is more extensive than in most conventional
        modalities. It includes standard clinical assessment — presenting
        problems, psychiatric history, psychosocial history, risk factors —
        but also PAT-specific screening: contraindication assessment
        (cardiovascular conditions, psychotic spectrum disorders, certain
        medication interactions), informed consent that covers the unique risks
        and nature of non-ordinary states, and readiness evaluation that goes
        beyond symptom severity to consider psychological preparedness,
        relational stability, and the client&rsquo;s capacity for surrender
        and self-regulation.
      </p>

      <p>
        The prompt templates for this phase generate structured intake
        documents that include all of these PAT-specific domains alongside
        the standard clinical intake fields. The AI does not guess what to
        include. The prompt tells it.
      </p>

      <h3>Phase 2: Preparation</h3>

      <p>
        Preparation sessions are a defining feature of PAT. They are where the
        therapeutic alliance deepens, intentions are explored and documented,
        psychological preparation occurs (including psychoeducation about the
        medicine experience and emotional safety planning), and set and setting
        considerations are assessed. This phase typically involves two to four
        sessions before the first dosing experience.
      </p>

      <p>
        Documentation templates for preparation cover intention setting,
        psychological readiness assessment, safety planning, and set and
        setting evaluation. Each template captures both the content of the
        preparation work and the clinician&rsquo;s assessment of the
        client&rsquo;s readiness to proceed.
      </p>

      <h3>Phase 3: Dosing and Ceremony</h3>

      <p>
        The dosing session is where PAT documentation departs most radically
        from conventional clinical writing. The clinician needs to document
        observations in real time or shortly after: the client&rsquo;s
        progression through non-ordinary states, somatic experiences (tremoring,
        postural changes, autonomic shifts), emotional content as it arises,
        any challenging material and how it was navigated, the quality and
        timing of the experience arc, and any interventions (verbal, somatic,
        musical, environmental) used during the session.
      </p>

      <p>
        Standard clinical note formats cannot capture this. The dosing
        observation templates provide structured fields for phenomenological
        documentation — what the experience looked like from the outside and,
        based on the client&rsquo;s verbal and non-verbal communication, what
        it appeared to involve from the inside. These templates also include
        fields for documenting the physical setting, substances administered,
        dose and timing, vital sign monitoring, and any adverse events.
      </p>

      <h3>Phase 4: Integration</h3>

      <p>
        Integration is where meaning-making happens. It is often the most
        therapeutically rich phase of PAT, and the documentation needs to
        reflect that richness. Integration session notes must capture the
        client&rsquo;s evolving understanding of their dosing experience,
        emergent insights and how they connect to presenting problems,
        somatic and emotional processing that continues after the session,
        behavioural changes (or resistance to change), and the relationship
        between the experience and the client&rsquo;s broader life narrative.
      </p>

      <p>
        The integration templates generate SOAP-structured notes that
        include PAT-specific fields: experience processing, insight tracking,
        meaning-making progress, and connection to the original treatment
        intentions documented during the preparation phase.
      </p>

      <h3>Phase 5: Treatment Planning</h3>

      <p>
        Treatment planning in PAT is inherently multi-phase. A SMART-goal treatment plan for a PAT client needs to account for preparation goals, dosing session
        parameters, integration objectives, and longer-term maintenance. The
        treatment plan templates generate modality-specific plans that
        reference the client&rsquo;s intake data, preparation work, and — if
        treatment is already underway — dosing session outcomes and integration
        progress.
      </p>

      <h3>Phase 6: Progress Notes</h3>

      <p>
        Session-to-session documentation across the full treatment arc. The
        progress note templates adapt to the current phase: preparation-phase
        notes emphasise readiness indicators, dosing-phase notes capture
        phenomenological observations, and integration-phase notes track
        meaning-making and behavioural change. Outcome measures relevant to PAT
        (such as the Mystical Experience Questionnaire, Challenging Experience
        Questionnaire, or standard measures like the PHQ-9 and GAD-7) can be
        incorporated directly.
      </p>

      <h3>Phase 7: Discharge and Follow-Up</h3>

      <p>
        The final documentation phase ties together the entire treatment arc.
        Discharge summaries and completion reports synthesise the client&rsquo;s
        journey from intake through integration, documenting outcomes against
        the original presenting problems and treatment goals. Aftercare plans
        address ongoing integration support, relapse prevention, and any
        referrals. Referrer feedback letters communicate treatment outcomes in
        language appropriate for the referring clinician, who may or may not be
        familiar with PAT.
      </p>

      <h2>Example: How the Intention Setting Template Works</h2>

      <p>
        To make this concrete, let us walk through a single template in
        detail. The Intention Setting Documentation template is used during
        the preparation phase, typically across one or two sessions before a
        dosing experience. It generates a structured clinical document that
        captures the client&rsquo;s therapeutic intentions and the
        clinician&rsquo;s assessment of them.
      </p>

      <h3>The Prompt Structure</h3>

      <p>
        The template begins with a context block that tells the AI what kind
        of document it is writing, what clinical framework to use, and what
        information the clinician is providing. This is not a vague
        instruction like &ldquo;write an intention-setting note.&rdquo; It is
        a structured specification:
      </p>

      <ul>
        <li>
          <strong>Document type:</strong> Intention Setting Documentation for
          psychedelic-assisted therapy preparation phase.
        </li>
        <li>
          <strong>Therapeutic context:</strong> The client&rsquo;s presenting
          problems, relevant history, and the specific medicine and protocol
          being used.
        </li>
        <li>
          <strong>Session content:</strong> The clinician&rsquo;s notes from the
          preparation session — what the client expressed about their
          intentions, how those intentions connect to presenting problems, any
          ambivalence or concerns, and the clinician&rsquo;s observations.
        </li>
        <li>
          <strong>Therapeutic orientation:</strong> The modality or modalities
          the clinician works from (more on this below).
        </li>
      </ul>

      <h3>What the AI Generates</h3>

      <p>
        Given this structured input, the template directs the AI to generate a
        document with specific sections:
      </p>

      <ul>
        <li>
          <strong>Client-stated intentions:</strong> What the client articulated
          as their hopes, goals, or questions for the upcoming experience,
          documented in language that stays close to the client&rsquo;s own
          words.
        </li>
        <li>
          <strong>Connection to presenting problems:</strong> How the stated
          intentions relate to the clinical issues identified at intake.
        </li>
        <li>
          <strong>Depth and specificity assessment:</strong> The
          clinician&rsquo;s evaluation of whether the intentions are
          sufficiently explored, overly intellectualised, or still emerging.
        </li>
        <li>
          <strong>Somatic and emotional readiness:</strong> Observations about
          the client&rsquo;s embodied relationship to their intentions — are
          they speaking from the head or from a place of genuine felt sense?
        </li>
        <li>
          <strong>Potential challenging territory:</strong> Areas where the
          dosing experience may bring difficult material, based on the
          intentions and the clinical picture.
        </li>
        <li>
          <strong>Safety considerations:</strong> Any concerns about
          psychological safety related to the stated intentions, and how these
          have been addressed in preparation.
        </li>
        <li>
          <strong>Clinician assessment:</strong> An overall assessment of the
          intention-setting process, including readiness to proceed and any
          recommended further preparation.
        </li>
      </ul>

      <h3>How the Clinician Reviews and Endorses</h3>

      <p>
        The AI-generated document is a draft. It is never a finished clinical
        record. The clinician reviews every section, corrects any
        inaccuracies, adds nuance the AI could not capture, removes anything
        that does not reflect the actual clinical picture, and endorses the
        final document. This review-and-endorse step is not optional — it is
        the entire point. The AI saves the clinician the labour of structuring
        the document from scratch. The clinician provides the clinical
        judgement that makes it a good clinical note.
      </p>

      <p>
        In practice, this process typically takes five to eight minutes of
        clinician review time, compared to twenty to thirty minutes of writing
        from scratch. The result is a more structured, more thorough document
        than most clinicians would produce under time pressure — because the
        template ensures nothing is omitted.
      </p>

      <h2>Orientation-Aware Prompting</h2>

      <p>
        One of the most significant design features of the MycenAI prompt
        templates is orientation awareness. PAT practitioners rarely work
        from a single therapeutic modality. The field draws from Internal
        Family Systems, somatic experiencing, transpersonal psychology,
        psychodynamic therapy, Acceptance and Commitment Therapy,
        Cognitive-Behavioural Therapy, Dialectical Behaviour Therapy, and
        various contemplative and indigenous traditions. Most practitioners
        blend several of these approaches, and the blend often shifts across
        phases of treatment.
      </p>

      <p>
        This matters for documentation because different modalities use different language,
        different conceptual frameworks, and different ways of understanding
        what is happening in therapy. A somatic practitioner documents body
        sensations, movement impulses, and autonomic regulation. An IFS
        practitioner documents parts, protectors, exiles, and Self energy. A
        psychodynamic practitioner documents transference, defences, and
        unconscious material. A transpersonal practitioner documents
        spiritual emergence, archetypal imagery, and non-dual awareness.
      </p>

      <p>
        Generic AI prompts default to a vaguely CBT-inflected clinical
        language that does not serve practitioners working from other
        orientations. The MycenAI templates include an orientation parameter
        that adjusts the AI&rsquo;s output to match the practitioner&rsquo;s
        theoretical framework. When you specify IFS as your primary
        orientation, the generated documentation uses IFS language and
        conceptual structure. When you specify somatic experiencing, the
        documentation centres body-based observations and autonomic
        regulation frameworks.
      </p>

      <p>
        This is not cosmetic. The difference between documenting that a client
        &ldquo;experienced anxiety&rdquo; and documenting that a client
        &ldquo;noticed a protective part carrying fear about abandonment,
        which softened when approached with curiosity from Self&rdquo; is the
        difference between a note that says nothing and a note that captures
        the actual clinical work. The same session, documented from different
        orientations, will produce legitimately different — and equally
        valid — clinical records. Orientation-aware prompting ensures the
        documentation matches how the practitioner actually thinks about and
        practises the work.
      </p>

      <h3>Blended Approaches</h3>

      <p>
        Many PAT practitioners work from an explicitly integrative framework,
        drawing on multiple modalities within a single session. The templates
        accommodate this by allowing practitioners to specify a primary
        orientation with secondary influences. For example, a practitioner
        might specify somatic experiencing as their primary framework with IFS
        and transpersonal psychology as secondary orientations. The AI then
        generates documentation that leads with somatic language but
        incorporates parts language and transpersonal frameworks where the
        session content calls for it.
      </p>

      <p>
        This reflects the reality of PAT practice, where a dosing session
        might begin with somatic processing, move into parts work as specific
        emotional content emerges, and culminate in a transpersonal or
        mystical experience that requires entirely different clinical
        language to document accurately.
      </p>

      <h2>Document Chaining in PAT</h2>

      <p>
        If you have read about document chaining in
        conventional therapy documentation, the concept will be familiar. In
        PAT, it is even more critical — and more complex.
      </p>

      <p>
        Document chaining means that the output from one template becomes the
        input for the next. The intake assessment feeds into the treatment
        plan. The treatment plan feeds into the preparation session notes.
        The preparation notes — including the intention-setting
        documentation — feed into the dosing observation template. The dosing
        observations feed into the integration session notes. And the
        integration notes, together with everything before them, feed into
        the discharge summary.
      </p>

      <p>
        Each template in the MycenAI Clinical Prompt Library is designed to
        accept input from prior templates in the chain. This means:
      </p>

      <ul>
        <li>
          <strong>The treatment plan template</strong> references the
          presenting problems, contraindication screening, and readiness
          assessment from the intake. You do not re-enter this information.
          The chain carries it forward.
        </li>
        <li>
          <strong>The preparation session templates</strong> reference the
          treatment goals and the client&rsquo;s specific clinical picture.
          The intention-setting documentation is automatically contextualised
          against the presenting problems documented at intake.
        </li>
        <li>
          <strong>The dosing observation template</strong> references the
          client&rsquo;s stated intentions from the preparation phase, so the
          clinician can document whether and how the experience connected to
          those intentions.
        </li>
        <li>
          <strong>The integration session templates</strong> reference both
          the original intentions and the dosing observations, creating a
          natural structure for tracking how the experience is being processed
          and integrated over time.
        </li>
        <li>
          <strong>The discharge summary template</strong> draws from the
          entire chain — intake baseline, treatment goals, preparation work,
          dosing outcomes, integration progress — to produce a comprehensive
          end-of-treatment document.
        </li>
      </ul>

      <p>
        The clinical value of this chaining is significant. In PAT, where
        treatment arcs can span months and a single dosing session can
        produce material that takes weeks to integrate, it is easy for the
        documentation to lose coherence. Earlier themes get forgotten. The
        connection between a client&rsquo;s intake presentation and their
        integration work six months later gets lost in the volume of session
        notes. Chaining prevents this by ensuring that each document in the
        record explicitly references the documents that came before it.
      </p>

      <h3>Practical Example: From Intake to Discharge</h3>

      <p>
        Consider a client who presents at intake with treatment-resistant
        depression, early relational trauma, and chronic somatic tension. The
        intake template captures all of this in structured form. When the
        clinician generates a treatment plan, the template automatically
        pulls forward the presenting problems and formulation, structuring
        the plan around preparation goals (building somatic awareness,
        exploring relational patterns), dosing session parameters (substance,
        dose, setting, support team), and integration objectives (processing
        trauma material, developing new relational patterns, sustained
        symptom reduction).
      </p>

      <p>
        During preparation, the intention-setting template contextualises the
        client&rsquo;s stated intentions against the clinical picture: the
        client&rsquo;s intention to &ldquo;understand why I feel so
        disconnected&rdquo; is documented in relation to the early relational
        trauma and the somatic holding patterns identified at intake. After
        the dosing session, the observation template links what happened in
        the room to those intentions and that clinical context. And when
        integration begins, the session notes track meaning-making against
        the entire chain: the original presenting problems, the treatment
        goals, the stated intentions, and the actual experience.
      </p>

      <p>
        When it is time to write the discharge summary, the clinician does
        not need to re-read fifty pages of notes. The chain has maintained
        the narrative thread throughout. The AI can generate a comprehensive
        summary that traces the client&rsquo;s journey from intake to
        completion, with the clinician reviewing and endorsing the final
        document.
      </p>

      <h2>Common Mistakes With Clinical AI Prompts</h2>

      <p>
        Before discussing how to get started, it is worth naming the most
        common errors practitioners make when using AI for PAT documentation
        — even with good templates.
      </p>

      <p>
        <strong>Using the AI as a scribe instead of a structuring tool.</strong>
        {" "}The templates are not designed to transcribe what happened in
        session. They are designed to structure the clinician&rsquo;s input
        into well-organised clinical documentation. The clinician still needs
        to provide the substance — what the client said, what the clinician
        observed, what clinical judgements were made. The AI organises and
        formats this material. If you give it nothing, you get nothing useful
        back.
      </p>

      <p>
        <strong>
          Skipping the review step.
        </strong>{" "}
        AI-generated documentation always requires clinician review. In PAT,
        where the clinical phenomena are unfamiliar to the AI&rsquo;s training
        data, this is especially important. The AI may use language that is
        close but not quite right. It may overemphasise one aspect of the
        session and underrepresent another. The review step is where clinical
        expertise enters the document. Skipping it creates records that are
        technically fluent but clinically hollow.
      </p>

      <p>
        <strong>Not specifying the therapeutic orientation.</strong> If you
        do not tell the template what orientation you work from, the AI will
        default to generic clinical language. This is particularly costly in
        PAT, where the practitioner&rsquo;s orientation shapes not just the
        language of documentation but the conceptual framework for
        understanding the client&rsquo;s experience. Always specify your
        orientation. If you work from a blended approach, specify the blend.
      </p>

      <p>
        <strong>Treating each document as standalone.</strong> The templates
        are designed to chain. If you use the dosing observation template
        without feeding in the preparation notes, you lose the contextual
        thread that makes the documentation coherent. Take the extra thirty
        seconds to include the prior document in the chain. The output
        quality improves dramatically.
      </p>

      <h2>Getting Started</h2>

      <p>
        The MycenAI Clinical Prompt Library is a downloadable collection of
        structured prompt templates covering every phase of the PAT treatment
        arc. Each template is designed to work as a standalone tool or as
        part of a chained workflow. The library includes templates for intake
        and assessment, preparation (including intention setting,
        psychological preparation, and safety planning), dosing observation,
        integration sessions, treatment planning, progress notes, and
        discharge documentation.
      </p>

      <p>
        Every template includes clear instructions for what input to provide,
        what output to expect, and how to review and endorse the generated
        document. The orientation parameter is built into each template, so
        you can specify your therapeutic framework from the outset.
      </p>

      <p>
        If you are already using AI for clinical documentation, the templates
        will immediately improve the specificity and clinical relevance of
        your output. If you are new to AI-assisted documentation, the
        templates provide a structured starting point that avoids the most
        common pitfalls of generic prompting.
      </p>

      <p>
        For practitioners looking for a fully integrated platform — where the
        templates, document chaining, and clinical workflow are built into a
        secure, privacy-first environment — visit{" "}
        <a href="https://confideai.ai/welcome/mycenai" target="_blank" rel="noopener noreferrer">
          confideai.ai
        </a>. Confide AI provides the infrastructure for using these templates
        with clinical-grade security, so you can focus on the work that
        matters: helping your clients navigate some of the most profound
        therapeutic experiences available.
      </p>

      <hr />

      <p>
        Psychedelic-assisted therapy is reshaping mental health treatment. The
        documentation practices that support this work need to keep pace. PAT
        practitioners deserve tools that understand the unique structure of
        their clinical workflow — not generic templates borrowed from
        conventional therapy and stretched to fit. Structured, PAT-specific
        prompt templates are how we close that gap: better prompts, better
        documentation, better clinical records, better care.
      </p>
    </>
  );
};

export default PatPromptTemplates;
